import { watchState } from "@ngrx/signals";
import { ButtonModule } from "primeng/button";
import { PasswordModule } from 'primeng/password';
import { NgOptimizedImage } from '@angular/common';
import { Router, ActivatedRoute } from "@angular/router";
import { AuthState, AuthStore } from "@auth/stores/auth.store";
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { ResetPasswordBuildFormService } from '@auth/services/reset-password-build-form.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, inject, signal, OnInit, Injector, DestroyRef } from '@angular/core';
import {
  APP_BRAND_LOGO_ALT,
  APP_BRAND_NAME,
  APP_BRAND_ORG,
  APP_BRAND_TAGLINE,
} from '@shared/constants/app-brand.const';

@Component({
  selector: 'app-reset-password',
  imports: [
    ButtonModule,
    PasswordModule,
    NgOptimizedImage,
    ReactiveFormsModule,
  ],
  templateUrl: './reset-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly authStore = inject(AuthStore);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resetPasswordBuildFormService = inject(ResetPasswordBuildFormService);

  public readonly isSubmitting = signal(false);
  public readonly tokenInvalid = signal(false);
  public readonly resetToken = signal<string | null>(null);
  public readonly resetPasswordForm = this.resetPasswordBuildFormService.buildResetPasswordForm();

  readonly brandLogoAlt = APP_BRAND_LOGO_ALT;
  readonly brandName = APP_BRAND_NAME;
  readonly brandTagline = APP_BRAND_TAGLINE;
  readonly brandFooterLine = `${APP_BRAND_NAME} · ${APP_BRAND_ORG}`;

  /**
   * Getter para acceder al control de nueva contraseña del formulario.
   */
  get newPassword(): FormControl {
    return this.resetPasswordForm.controls['newPassword'] as FormControl;
  }

  /**
   * Getter para acceder al control de confirmación de contraseña del formulario.
   */
  get confirmPassword(): FormControl {
    return this.resetPasswordForm.controls['confirmPassword'] as FormControl;
  }

  /**
   * Método del ciclo de vida Angular que se ejecuta al inicializar el componente.
   */
  public ngOnInit(): void {
    this.redirectIfAuthenticated();
    this.extractTokenFromUrl();
    this.watchAuthStateChanges();
  }

  /**
   * Maneja el evento de envío del formulario de restablecimiento de contraseña.
   */
  public onSubmit(): void {
    if (this.resetPasswordForm.invalid || !this.resetToken()) return;

    const formValue = this.resetPasswordForm.value;
    this.authStore.resetPassword({
      token: this.resetToken()!,
      newPassword: formValue.newPassword
    });
  }

  /**
   * Navega de regreso a la página de login.
   */
  public goToLogin(): void {
    this.router.navigate(['/auth/login']);
  }

  /**
   * Redirige al usuario si ya está autenticado.
   */
  private redirectIfAuthenticated(): void {
    if (this.authStore.isAuthenticated()) {
      this.router.navigate(['/app/inicio']);
    }
  }

  /**
   * Extrae y valida el token de recuperación desde los parámetros de la URL.
   */
  private extractTokenFromUrl(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const token = params['token'];
      this.resetToken.set(token || null);
      this.tokenInvalid.set(!token);
    });
  }

  /**
   * Observa los cambios de estado en el store de autenticación.
   */
  private watchAuthStateChanges(): void {
    watchState(this.authStore, this.handleAuthStateChange.bind(this), {
      injector: this.injector
    });
  }

  /**
   * Maneja los cambios de estado de autenticación.
   */
  private handleAuthStateChange(authState: AuthState): void {
    if (authState.isLoading) {
      this.clearForm();
    }
    this.isSubmitting.set(authState.isLoading);
  }

  /**
   * Limpia el formulario restableciéndolo a sus valores iniciales.
   */
  private clearForm(): void {
    const initialValues = this.resetPasswordBuildFormService.buildResetPasswordForm().getRawValue();
    this.resetPasswordForm.reset(initialValues);
  }
}
